import { useSessionSelection } from '../useSessionSelection';
/* ============================================================
   Админка · Заявки на участие — реальные данные бэкенда
   (tournament applications через useAppStore).
   Фильтр по турниру + раскрывающиеся детали с ответами формы.
   ============================================================ */
import { Fragment, useState } from 'react';
import { API_URL, apiFetch, useAppStore } from '../../store';
import { useFedContent } from '../content';
import { Ic } from '../icons';
import { AdmCard, StPill, AdmIconBtn, EmptyRow } from './ui';

// Бэкенд-статусы заявок → статусы дизайна
export const regStatus = (s) =>
  s === 'verified' ? 'approved' : s === 'rejected' ? 'rejected' : 'new';

// База для ссылок на загруженные документы (uploads/ отдаёт бэкенд без /api)
const FILE_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5252/api').replace(/\/api$/, '');

// Документы заявок (uploads/tournament_<id>_…) отдаются бэкендом только
// владельцу заявки или админу — обычная <a href> не пронесёт токен, поэтому
// качаем с Authorization-заголовком и открываем как blob.
const openAuthedFile = async (path) => {
  try {
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${FILE_BASE}/${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    window.open(`${FILE_BASE}/${path}`, '_blank', 'noopener,noreferrer');
  }
};

/* Детали заявки: ответы формы (метки из схемы турнира) + легаси-поля CS2 */
function RegDetails({ app, tournaments }) {
  const schema = tournaments.find((t) => t.id === app.tournament_key)?.form?.fields || [];
  const labelOf = (key) => schema.find((f) => f.key === key)?.label_ru || key;
  const answers = app.answers && typeof app.answers === 'object' ? app.answers : null;

  const legacy = [
    ['ФИО', app.fio],
    ['Телефон', app.phone],
    ['Steam', app.steam_profile],
    ['Роль', app.role],
    ['Учёба', app.education_name && `${app.education_name}${app.grade ? ` · ${app.grade}` : ''}`],
  ].filter(([, v]) => v);

  const renderValue = (v) => {
    if (typeof v === 'boolean') return v ? 'Да' : 'Нет';
    if (typeof v === 'string' && v.startsWith('uploads/')) {
      return (
        <button type="button" onClick={() => openAuthedFile(v)} className="inline-flex items-center gap-1.5 text-accent-bright border-none bg-transparent cursor-pointer" style={{ fontSize: 13, padding: 0 }}>
          <Ic name="doc" size={14} /> открыть файл
        </button>
      );
    }
    return String(v);
  };

  const rows = answers
    ? Object.entries(answers).map(([k, v]) => [labelOf(k), renderValue(v)])
    : legacy.map(([l, v]) => [l, v]);

  return (
    <div className="grid gap-x-6 gap-y-2" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', padding: '4px 8px 8px' }}>
      {rows.map(([l, v], i) => (
        <div key={i} className="flex flex-col gap-0.5">
          <span className="font-mono uppercase text-fg-faint" style={{ fontSize: 9.5, letterSpacing: '.1em' }}>{l}</span>
          <span className="text-fg-soft" style={{ fontSize: 13.5, wordBreak: 'break-word' }}>{v}</span>
        </div>
      ))}
      {!answers && app.document_file && (
        <div className="flex flex-col gap-0.5">
          <span className="font-mono uppercase text-fg-faint" style={{ fontSize: 9.5, letterSpacing: '.1em' }}>Документ</span>
          <button type="button" onClick={() => openAuthedFile(app.document_file)} className="inline-flex items-center gap-1.5 text-accent-bright border-none bg-transparent cursor-pointer" style={{ fontSize: 13, padding: 0 }}>
            <Ic name="doc" size={14} /> открыть файл
          </button>
        </div>
      )}
      {!rows.length && !app.document_file && <span className="text-fg-faint" style={{ fontSize: 13 }}>Подробностей нет</span>}
    </div>
  );
}

function TeamRegDetails({ team }) {
  return (
    <div style={{ padding: '6px 8px 10px' }}>
      <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 10 }}>
        <span className="font-display font-bold text-fg" style={{ fontSize: 15 }}>{team.name}</span>
        <span className="font-mono uppercase text-fg-muted" style={{ fontSize: 10 }}>{team.tag} · код {team.id}</span>
      </div>
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))' }}>
        {(team.members || []).map((member, index) => (
          <div key={member.key} className="rounded-xl border border-line" style={{ padding: '10px 12px', background: 'var(--fed-surface)' }}>
            <div className="font-semibold text-fg" style={{ fontSize: 13.5 }}>
              {index + 1}. {member.nick || '—'} {team.captain === member.key ? '· капитан' : ''}
            </div>
            <div className="font-mono text-fg-faint" style={{ fontSize: 10.5, marginTop: 4, wordBreak: 'break-all' }}>Steam: {member.gameId || '—'}</div>
            <div className="font-mono text-fg-faint" style={{ fontSize: 10.5, marginTop: 2 }}>{member.tg || '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdmRegs({ regs, reload }) {
  const updateApplicationStatus = useAppStore((s) => s.updateApplicationStatus);
  const fire = useAppStore((s) => s.fire);
  const tournaments = useFedContent((s) => s.tournaments);
  const [tourFilter, setTourFilter] = useSessionSelection('admin-regs-filter', 'all');
  const [openId, setOpenId] = useState(null);
  const [savingFee, setSavingFee] = useState(null);

  const updateFee = async (r, feeConfirmed) => {
    setSavingFee(r.id);
    try {
      const res = await apiFetch(`${API_URL}/fed-teams/teams/${encodeURIComponent(r.rawTeam.id)}/fee`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        body: JSON.stringify({ tour: r.tournamentKey, feeConfirmed }),
      });
      if (!res.ok) throw new Error('fee-update-failed');
      await reload?.();
    } catch (e) {
      fire?.('Не удалось обновить статус взноса', 'error');
    } finally {
      setSavingFee(null);
    }
  };

  const act = async (id, status) => {
    try {
      await updateApplicationStatus(id, status, status === 'rejected' ? 'Отклонено администратором' : null);
      reload && reload();
    } catch (e) {
      fire && fire('Не удалось обновить статус', 'error');
    }
  };

  const shown = regs.filter((r) => {
    if (tourFilter === 'all') return true;
    if (tourFilter === 'legacy') return !r.tournamentKey;
    return r.tournamentKey === tourFilter;
  });

  return (
    <div className="fed-admin-view flex flex-col gap-4">
      <div className="flex items-center gap-2.5 flex-wrap">
        <span className="fed-label">Турнир:</span>
        <select
          className="fed-input"
          style={{ height: 40, maxWidth: 320 }}
          value={tourFilter}
          onChange={(e) => setTourFilter(e.target.value)}
        >
          <option value="all">Все турниры</option>
          {tournaments.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
          <option value="legacy">Старые заявки (без турнира)</option>
        </select>
        <span className="font-mono text-fg-faint" style={{ fontSize: 11 }}>{shown.length} шт.</span>
      </div>

      <AdmCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="fed-tbl">
            <thead>
              <tr><th>Игрок</th><th>Турнир</th><th>Контакт</th><th>Дата</th><th>Статус</th><th>Действия</th></tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <Fragment key={r.id}>
                  <tr onClick={() => setOpenId(openId === r.id ? null : r.id)} style={{ cursor: 'pointer' }}>
                    <td data-wide>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center rounded-lg font-mono font-bold text-fg bg-surface-3 flex-shrink-0" style={{ width: 32, height: 32, fontSize: 13 }}>
                          {(r.nick || '?')[0].toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <div className="font-semibold text-fg" style={{ fontSize: 14 }}>{r.nick}</div>
                          {r.name && r.name !== r.nick ? <div className="font-mono text-fg-faint" style={{ fontSize: 10.5, marginTop: 2 }}>{r.name}</div> : null}
                        </div>
                      </div>
                    </td>
                    <td data-label="Турнир" style={{ maxWidth: 180 }}>{r.tournament}</td>
                    <td data-label="Контакт" className="font-mono" style={{ fontSize: 12.5 }}>{r.contact}</td>
                    <td data-label="Дата" className="font-mono" style={{ whiteSpace: 'nowrap' }}>{r.date}</td>
                    <td data-label="Статус">
                      <StPill st={r.status} />
                      {r.hasFeeStatus && <div style={{ marginTop: 6, fontSize: 12, color: r.feeConfirmed ? 'var(--fed-ok)' : 'var(--fed-gold)' }}>
                        {r.feeConfirmed ? 'Взнос подтвержден' : 'Взнос не подтвержден'}
                      </div>}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {r.kind === 'team' ? (
                        r.hasFeeStatus ? <button className="fed-btn-ghost px-3 py-2" disabled={savingFee !== null} onClick={() => updateFee(r, !r.feeConfirmed)}>
                          {savingFee === r.id ? 'Сохранение…' : r.feeConfirmed ? 'Отменить подтверждение взноса' : 'Подтвердить взнос'}
                        </button> : <span className="font-mono uppercase text-fg-faint" style={{ fontSize: 9.5 }}>Командная заявка</span>
                      ) : (
                        <div className="flex gap-2">
                          <AdmIconBtn name="check" tint="ok" title="Принять" onClick={() => act(r.id, 'verified')} />
                          <AdmIconBtn name="x" tint="danger" title="Отклонить" onClick={() => act(r.id, 'rejected')} />
                        </div>
                      )}
                    </td>
                  </tr>
                  {openId === r.id && r.rawTeam && (
                    <tr>
                      <td colSpan={6} style={{ background: 'var(--fed-surface-2)' }}>
                        <TeamRegDetails team={r.rawTeam} />
                      </td>
                    </tr>
                  )}
                  {openId === r.id && r.raw && (
                    <tr>
                      <td colSpan={6} style={{ background: 'var(--fed-surface-2)' }}>
                        <RegDetails app={r.raw} tournaments={tournaments} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {!shown.length && <EmptyRow colSpan={6}>Заявок нет (или бэкенд недоступен)</EmptyRow>}
            </tbody>
          </table>
        </div>
      </AdmCard>
    </div>
  );
}
